import { serializeJson } from '../lib/serializeJson';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCategories, useProduct } from '../features/products/api';
import { buildSeo } from '@skytech/shared/seo.cjs';
import { item, pageView, track } from '../features/analytics/analytics';

function meta(name: string, content: string, property = false) {
  const key = property ? 'property' : 'name';
  let node = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${name}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(key, name);
    document.head.append(node);
  }
  node.content = content;
}
/** SRS Appendix A.7: route metadata and truthful public product structured data. */
export function Seo(): null {
  const { pathname, search } = useLocation();
  const slug = /^\/products\/([^/]+)$/.exec(pathname)?.[1];
  const { data: product, isLoading: productLoading, isError } = useProduct(slug);
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const [consentVersion, setConsentVersion] = useState(0);
  useEffect(() => {
    const update = () => setConsentVersion((v) => v + 1);
    window.addEventListener('skytech-consent', update);
    return () => window.removeEventListener('skytech-consent', update);
  }, []);
  useEffect(() => {
    // Preserve correct server metadata while route-specific data is loading.
    if (
      (slug && productLoading) ||
      ((pathname.startsWith('/category/') || new URLSearchParams(search).has('category')) &&
        categoriesLoading)
    )
      return;
    const doc = buildSeo({
      path: pathname + search,
      product,
      categories,
      unavailable: Boolean(slug && isError),
    });
    const { title, description, canonical } = doc;
    document.title = title;
    meta('description', description);
    meta('robots', doc.robots);
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.append(link);
    }
    link.href = canonical;
    meta('og:title', title, true);
    meta('og:description', description, true);
    meta('og:url', canonical, true);
    meta('og:type', doc.type, true);
    meta('og:image', doc.image, true);
    meta('og:locale', 'en_KE', true);
    meta('twitter:card', 'summary_large_image');
    meta('twitter:title', title);
    meta('twitter:description', description);
    meta('twitter:image', doc.image);
    let schema = document.getElementById('skytech-structured-data');
    if (!schema) {
      schema = document.createElement('script');
      schema.setAttribute('type', 'application/ld+json');
      schema.id = 'skytech-structured-data';
      document.head.append(schema);
    }
    schema.textContent = serializeJson(doc.schema);
    if (!slug || product) pageView(pathname, title);
    if (product)
      track(
        'view_item',
        { currency: 'KES', value: Number(product.price), items: [item(product)] },
        `view:${product.id}:${pathname}`,
      );
  }, [
    pathname,
    search,
    product,
    categories,
    slug,
    isError,
    productLoading,
    categoriesLoading,
    consentVersion,
  ]);
  return null;
}
